import { add, getUnixTime } from 'date-fns';
import type { SearchResponse } from 'instantsearch.js';
import { useEffect, useState } from 'react';

import type { Sport } from '#src/__generated__/globalTypes.ts';
import type { AlgoliaCardIndexes } from '#src/contexts/config/index.ts';
import { useConfigContext } from '#src/contexts/config/index.ts';
import { useMemoArray } from '#src/hooks/useMemoArray.ts';
import {
  joinFiltersWithAnd,
  joinFiltersWithOr,
  sportFilter,
} from '#src/lib/algolia.ts';

import { useSearchClient } from './useSearchClient.ts';

type Props = {
  index: keyof AlgoliaCardIndexes;
  teamSlug?: string | null;
  competitionSlugs?: string[];
  playerSlugs?: string[];
  excludedPlayerSlugs?: string[];
  specialEdition?: string;
  rarities?: string[];
  season?: string | number;
  inSeasonEligible?: boolean;
  sport?: Sport;
  primaryMarket?: boolean;
  withBundled?: boolean;
  limit?: number;
  minMinutesBeforeSaleEnd?: number;
  distinct?: boolean;
  skip?: boolean;
  primaryOffer?: boolean;
  activeLeagues?: string[];
  addtionalAlgoliaFilters?: string;
  optionalFilters?: string | string[];
};

export enum SaleType {
  EnglishAuction = 'EnglishAuction',
  PrimaryOffer = 'PrimaryOffer',
  SingleSaleOffer = 'SingleSaleOffer',
}

type HitOnSale = {
  slug: string;
  sale: {
    type: SaleType;
  };
};

export const useCardsOnSale = (props: Props) => {
  const {
    index,
    teamSlug,
    competitionSlugs = [],
    playerSlugs = [],
    excludedPlayerSlugs = [],
    specialEdition,
    rarities = [],
    season,
    inSeasonEligible,
    primaryMarket,
    withBundled,
    limit,
    sport,
    minMinutesBeforeSaleEnd,
    distinct = false,
    skip = false,
    addtionalAlgoliaFilters,
    primaryOffer = false,
    optionalFilters,
  } = props;
  const [cards, setCards] = useState<string[]>([]);
  const [auctions, setAuctions] = useState<string[]>([]);
  const [primaryOffers, setPrimaryOffers] = useState<string[]>([]);
  const [secondaryOffers, setSingleSaleOffers] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [nbHits, setNbHits] = useState<number>(0);
  const searchClient = useSearchClient();
  const { algoliaIndexes } = useConfigContext();

  // Necessary to avoid infinite loop
  const raritiesStringified = rarities.join(',');
  const playerSlugsStringified = playerSlugs.join(',');
  const excludedPlayerSlugsStringified = excludedPlayerSlugs.join(',');
  const competitionSlugsStringified = competitionSlugs.join(',');

  useEffect(() => {
    const i = setInterval(() => {
      searchClient.clearCache();
    }, 30000);
    return () => clearInterval(i);
  }, [searchClient]);

  useEffect(() => {
    if (skip) return;
    const parsedRarities = raritiesStringified.split(',');
    const parsedPlayerSlugs = playerSlugsStringified.split(',');
    const parsedExcludedPlayerSlugs = excludedPlayerSlugsStringified.split(',');
    const parsedCompetitionSlugs = competitionSlugsStringified?.split(',');
    const isPrimaryMarket = primaryMarket !== undefined;
    const isInstantBuy = !isPrimaryMarket && primaryOffer;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true);
    searchClient
      .search<HitOnSale>([
        {
          indexName: algoliaIndexes[index],
          type: 'default',
          params: {
            analytics: false,
            distinct,
            hitsPerPage: limit || 10,
            attributesToRetrieve: ['slug', 'asset_id', 'sale.type'],
            optionalFilters,
            facets: [],
            filters: joinFiltersWithAnd(
              [
                sport && sportFilter(sport),
                'on_sale:true',
                specialEdition && `card_edition.display_name:${specialEdition}`,
                minMinutesBeforeSaleEnd &&
                  `sale.end_date > ${getUnixTime(
                    add(new Date(), { minutes: minMinutesBeforeSaleEnd })
                  )}`,
                isPrimaryMarket &&
                  joinFiltersWithAnd([`sale.primary:true`, `on_sale:true`]),
                withBundled !== undefined && `sale.bundled:${!!withBundled}`,
                playerSlugsStringified &&
                  joinFiltersWithOr(
                    parsedPlayerSlugs.map(
                      (playerSlug: string) => `player.slug:${playerSlug}`
                    )
                  ),
                excludedPlayerSlugsStringified &&
                  joinFiltersWithAnd(
                    parsedExcludedPlayerSlugs.map(
                      (playerSlug: string) => `NOT player.slug:${playerSlug}`
                    )
                  ),
                teamSlug && `team.slug:${teamSlug}`,
                raritiesStringified &&
                  joinFiltersWithOr(
                    parsedRarities.map((rarity: string) => `rarity:${rarity}`)
                  ),
                season && `season:${season}`,
                !!inSeasonEligible && 'in_season_eligible:true',
                isInstantBuy &&
                  joinFiltersWithAnd([
                    'sale.primary:true',
                    'sale.type:PrimaryOffer',
                  ]),
                addtionalAlgoliaFilters,
                competitionSlugsStringified &&
                  joinFiltersWithOr(
                    parsedCompetitionSlugs.map(
                      (competitionSlug: string) =>
                        `active_leagues.slug:${competitionSlug}`
                    )
                  ),
              ].filter(Boolean)
            ),
          },
        },
      ])
      .then(({ results }) => {
        setCards(
          (results[0] as SearchResponse<HitOnSale>).hits.map(hit => hit.slug)
        );
        setAuctions(
          (results[0] as SearchResponse<HitOnSale>).hits
            .filter(hit => hit?.sale?.type === SaleType.EnglishAuction)
            .map(hit => hit.slug)
        );
        setPrimaryOffers(
          (results[0] as SearchResponse<HitOnSale>).hits
            .filter(hit => hit?.sale?.type === SaleType.PrimaryOffer)
            .map(hit => hit.slug)
        );
        setSingleSaleOffers(
          (results[0] as SearchResponse<HitOnSale>).hits
            .filter(hit => hit?.sale?.type === SaleType.SingleSaleOffer)
            .map(hit => hit.slug)
        );
        setNbHits((results[0] as SearchResponse<HitOnSale>).nbHits);
        setLoading(false);
      });
  }, [
    algoliaIndexes,
    index,
    limit,
    playerSlugsStringified,
    primaryMarket,
    primaryOffer,
    raritiesStringified,
    season,
    searchClient,
    sport,
    teamSlug,
    withBundled,
    distinct,
    skip,
    minMinutesBeforeSaleEnd,
    optionalFilters,
    specialEdition,
    inSeasonEligible,
    addtionalAlgoliaFilters,
    excludedPlayerSlugsStringified,
    competitionSlugsStringified,
  ]);

  return {
    loading,
    slugs: useMemoArray(cards),
    auctionSlugs: useMemoArray(auctions),
    primaryOfferSlugs: useMemoArray(primaryOffers),
    secondaryOfferSlugs: useMemoArray(secondaryOffers),
    nbHits,
  };
};
